import { isBrowser } from 'es-toolkit';
import { statuses as couponStatuses } from '../constants/subscription/coupon';
import { decorateProductsWithCoupons } from '../data/subscription/couponDecorators';
import { getProductsBySkus as loadProductsBySkus } from '../data/subscription/getProductsBySkus';
import { selectCouponCacheConfig } from '../selectors/config';
import shouldUseStripe from '../utils/subscription/shouldUseStripe';
import { logClientError } from './logging';
import { fetchCoupon } from './subscription';
import getCountryCode from './utils/getCountryConfig';
import getDisabledCacheConfig from './utils/getDisabledCacheConfig';

export const GET_PRODUCTS_BY_SKUS = 'GET_PRODUCTS_BY_SKUS';
export const PRODUCTS_TRANSFORM = 'PRODUCTS_TRANSFORM';
export const UPDATE_SKU_RENEW_STATUSES = 'UPDATE_SKU_RENEW_STATUSES';
const skuFetchError = 'SKU FETCH ERROR';

function transformProducts(data, skuMeta, options = {}) {
  return async (dispatch, getState) => {
    const dataArray = Array.isArray(data) ? data : [data];

    if (options.skipDecoration) {
      return dispatch({
        type: PRODUCTS_TRANSFORM,
        meta: {
          skuMeta,
        },
        content: dataArray,
      });
    }

    const couponData = getState()?.subscription?.coupon;
    const decoratedProducts = await decorateProductsWithCoupons({
      products: dataArray,
      couponFromState: couponData,
      fetchDefaultCoupon: async (product) => {
        if (!product.defaultCouponCode) {
          return undefined;
        }

        const couponResponse = await dispatch(
          fetchCoupon(product.defaultCouponCode, couponStatuses.redeemable, true),
        );
        return couponResponse?.value?.data;
      },
      logger: { dispatch },
    });

    return dispatch({
      type: PRODUCTS_TRANSFORM,
      meta: {
        skuMeta,
      },
      content: decoratedProducts,
    });
  };
}

function getProductsBySkusEffect(skus, countryCode, skuMeta) {
  return async (dispatch, getState, extraArgument = {}) => {
    const state = getState();
    const transport = extraArgument?.transport;
    const isStripe = shouldUseStripe(state);
    const disableCache = getDisabledCacheConfig(state);
    const disableCouponCache = selectCouponCacheConfig(state);
    const runningOnServer = !isBrowser();

    async function getPayload() {
      const content = await loadProductsBySkus(
        {
          transport,
          request: {
            isServer: runningOnServer,
          },
        },
        {
          skus,
          countryCode,
          disableCache,
          disableCouponCache,
          isStripe,
          coupon: {
            state: state.subscription?.coupon ?? undefined,
          },
        },
      );

      return { content };
    }

    return await dispatch({
      type: GET_PRODUCTS_BY_SKUS,
      meta: {
        skuMeta,
        contentId: 'productFetchStatus',
      },
      payload: getPayload(),
    });
  };
}

/**
 * @param {string[]} skus - array of skus to be updated
 * @param {boolean} isRenew - new isRenew value
 * @returns {object}
 */
export function updateSkuRenewStatuses(skus, isRenew) {
  return {
    type: UPDATE_SKU_RENEW_STATUSES,
    meta: {
      skus,
      isRenew,
    },
  };
}

/**
 * @param {string[]} skus - array of all the skus
 * @param {Object} meta -
  {
    defaultSkus: ['sku1', 'sku2'],
    fromQuery: ['sku3', 'sku4'],
    isRenew: false,
  }
 * @returns {Function}
 */
export function getProductsBySkus(skus, meta) {
  return async (dispatch, getState, extraArgument = {}) => {
    const state = getState();
    const products = skus.map((sku) => state.products[sku]).filter(Boolean);

    if (skus.length === products.length) {
      const skusRequiringUpdate = products
        .filter(({ isRenew }) => isRenew !== meta?.isRenew)
        .map(({ sku }) => sku);

      if (skusRequiringUpdate.length) {
        dispatch(updateSkuRenewStatuses(skusRequiringUpdate, meta?.isRenew));
      }

      return Promise.resolve();
    }

    const countryCode = getCountryCode(state);
    const transport = extraArgument?.transport;

    try {
      const productsResponse = await dispatch(getProductsBySkusEffect(skus, countryCode, meta));
      const responseContent = productsResponse?.value?.content ?? productsResponse?.content;
      const skipDecoration = Boolean(transport?.getProductsBySkus);
      return await dispatch(transformProducts(responseContent, meta, { skipDecoration }));
    } catch (error) {
      dispatch(
        logClientError({
          message: `${skuFetchError}: ${error.message}`,
          context: {
            skus,
            error,
          },
        }),
      );

      throw error;
    }
  };
}
